import type { Motors } from "gql/api";
import type { EquipmentTableColumn } from "@/composables/equipment/equipmentTypes";

export type MotorRowData = Motors & {
  nameplate_horsepower_50?: number | null;
  nameplate_voltage_50?: number | null;
};

export const motorTableColumns: EquipmentTableColumn<MotorRowData>[] = [
  {
    editable: true,
    field: "include_koc_timestamp",
    header: "Include KOC",
    sortable: true,
    isCheckbox: true,
    adminOnly: true
  },
  {
    editable: true,
    field: "only_koc_timestamp",
    header: "KOC Only",
    sortable: true,
    isCheckbox: true,
    adminOnly: true
  },
  {
    editable: true,
    field: "include_ecopetrol_timestamp",
    header: "Include Ecopetrol",
    sortable: true,
    isCheckbox: true,
    adminOnly: true
  },
  {
    editable: true,
    field: "only_ecopetrol_timestamp",
    header: "Ecopetrol Only",
    sortable: true,
    isCheckbox: true,
    adminOnly: true
  },

  {
    sortable: true,
    editable: true,
    field: "manufacturer",
    header: "Manufacturer",
    searchable: true
  },
  {
    editable: true,
    sortable: true,
    field: "part_number",
    header: "Motor Series / Model",
    searchable: true
  },
  {
    editable: true,
    sortable: true,
    field: "outer_diameter_inches",
    header: "Outer Diameter (in)",
    isNumber: true,
    searchable: true,
    decimalPlaces: 6
  },
  {
    editable: true,
    sortable: true,
    field: "inner_diameter_inches",
    header: "Inner Diameter (in)",
    isNumber: true,
    searchable: true,
    decimalPlaces: 6,
    adminOnly: true
  },
  {
    editable: true,
    field: "neck_outer_diameter_inches",
    header: "Neck Outer Diameter (in)",
    isNumber: true,
    sortable: true,
    searchable: true,
    decimalPlaces: 6,
    adminOnly: true
  },
  {
    editable: true,
    field: "neck_inner_diameter_inches",
    header: "Neck Inner Diameter (in)",
    isNumber: true,
    sortable: true,
    searchable: true,
    decimalPlaces: 6,
    adminOnly: true
  },
  {
    editable: true,
    field: "outer_diameter_rub_inches",
    header: "Outer Diameter + Rub (in)",
    decimalPlaces: 6,
    isNumber: true,
    adminOnly: true,
    searchable: true
  },
  {
    editable: true,
    sortable: true,
    field: "length_inches",
    header: "Length (in)",
    isNumber: true,
    adminOnly: true,
    searchable: true
  },
  {
    editable: true,
    sortable: true,
    field: "weight_pounds",
    header: "Weight (lb)",
    isNumber: true,
    adminOnly: true,
    searchable: true
  },
  {
    editable: true,
    sortable: true,
    field: "mle_offset_depth_inches",
    header: "MLE Offset Depth (in)",
    decimalPlaces: 4,
    isNumber: true,
    adminOnly: true,
    searchable: true
  },
  {
    editable: true,
    sortable: true,
    field: "nameplate_horsepower",
    header: "Nameplate 60hz (HP)",
    isNumber: true,
    searchable: true
  },
  {
    editable: true,
    sortable: true,
    field: "nameplate_voltage",
    header: "Nameplate 60hz (V)",
    isNumber: true,
    searchable: true
  },
  {
    editable: false,
    sortable: true,
    field: "nameplate_horsepower_50",
    header: "Nameplate 50hz (HP)",
    isNumber: true,
    searchable: true
  },
  {
    editable: false,
    sortable: true,
    field: "nameplate_voltage_50",
    header: "Nameplate 50hz (V)",
    isNumber: true,
    searchable: true
  },
  {
    editable: true,
    sortable: true,
    field: "nameplate_current",
    header: "Nameplate Current (Amp)",
    isNumber: true,
    searchable: true
  },
  {
    editable: true,
    sortable: true,
    field: "poles",
    header: "Poles",
    isNumber: true,
    searchable: true,
    adminOnly: true
  },
  {
    editable: true,
    sortable: true,
    field: "configuration",
    header: "Configuration",
    adminOnly: true
  },
  {
    editable: true,
    sortable: true,
    field: "shaft_horsepower",
    header: "Shaft Horsepower",
    isNumber: true,
    adminOnly: true,
    searchable: true
  },
  {
    editable: true,
    sortable: true,
    field: "temperature_rating_fahrenheit",
    header: "Temperature Rating (F)",
    isNumber: true,
    adminOnly: false,
    searchable: true
  },
  {
    editable: true,
    sortable: true,
    field: "allow_rerate",
    header: "Allow Rerate",
    isDropdown: true,
    adminOnly: true
  },
  {
    editable: true,
    sortable: true,
    field: "oil_volume_gallons",
    header: "Oil Volume (gal)",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 10
  },
  {
    editable: true,
    sortable: true,
    field: "nb",
    header: "Nb",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "n1",
    header: "N1",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "n2",
    header: "N2",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "n3",
    header: "N3",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "n4",
    header: "N4",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "n5",
    header: "N5",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "ib",
    header: "Ib",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "i1",
    header: "I1",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "i2",
    header: "I2",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "i3",
    header: "I3",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "i4",
    header: "I4",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "i5",
    header: "I5",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "pfb",
    header: "PFB",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "pf1",
    header: "PF1",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "pf2",
    header: "PF2",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "pf3",
    header: "PF3",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "pf4",
    header: "PF4",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "pf5",
    header: "PF5",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "effb",
    header: "EFFB",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "eff1",
    header: "EFF1",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "eff2",
    header: "EFF2",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "eff3",
    header: "EFF3",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "eff4",
    header: "EFF4",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "eff5",
    header: "EFF5",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "rr_lb",
    header: "RR LB",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "rr_l1",
    header: "RR L1",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "rr_l2",
    header: "RR L2",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "stator_inner_diameter_inches",
    header: "Stator Inner Diameter (in)",
    isNumber: true,
    decimalPlaces: 5,
    adminOnly: true
  },
  {
    editable: true,
    sortable: true,
    field: "stator_outer_diameter_inches",
    header: "Stator Outer Diameter (in)",
    isNumber: true,
    decimalPlaces: 5,
    adminOnly: true
  },
  {
    editable: true,
    sortable: true,
    field: "nameplate_rerate_ratio",
    header: "Nameplate Rerate Ratio",
    isNumber: true,
    adminOnly: true
  },
  {
    editable: true,
    sortable: true,
    field: "k3",
    header: "K3",
    isNumber: true,
    adminOnly: true
  },
  {
    editable: true,
    sortable: true,
    field: "rr_vb",
    header: "RR VB",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "rr_v1",
    header: "RR V1",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "rr_v2",
    header: "RR V2",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "rr_v3",
    header: "RR V3",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "nv0",
    header: "NV0",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "nv1",
    header: "NV1",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "nv2",
    header: "NV2",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "iv0",
    header: "IV0",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "iv1",
    header: "IV1",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "iv2",
    header: "IV2",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "pfv0",
    header: "PFV0",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "pfv1",
    header: "PFV1",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "pfv2",
    header: "PFV2",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "effv0",
    header: "EFFV0",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "effv1",
    header: "EFFV1",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "effv2",
    header: "EFFV2",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "tv0",
    header: "TV0",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "tv1",
    header: "TV1",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    sortable: true,
    field: "tv2",
    header: "TV2",
    isNumber: true,
    adminOnly: true,
    decimalPlaces: 25
  }
];
